import { Link } from 'react-router-dom';
import { ProductImage } from '../../ui/ProductImage';
import { AddButton } from '../shared/AddButton';
import { useCatalog } from '../../contexts/CatalogContext';
import { creators } from '../../data/creators';
import { founder } from '../../data/content';
import { shopLabels } from '../../data/shopLabels';
import { euro } from '../../core/format/currency';
import { isSoldOut } from '../../core/rules/stock';
import { sectionHeading } from '../../ui/sections';
import { fill } from '../../utils/copy';
import { Section } from '../shared/Section';
import { PressStrip, hasPress } from '../shared/PressStrip';
type Props = {
  heading: string;
  body: string;
};

/**
 * "Wie het ook gebruiken" op Over (docs/VORMGEVING.md §3): de perslijn bovenaan, daaronder vaste
 * cliënten en hun favoriete product, met hun code. Samen het bewijs bij het verhaal van Sarah, op
 * één zandvlak. Een cliënt zonder bestaand product valt weg; de lijst zelf staat in
 * Instellingen › Cadeaus & codes. Kop en zin: Paginateksten › Over.
 */
export function CreatorRail({
  heading,
  body
}: Props) {
  const {
    productById
  } = useCatalog();
  const picks = creators.flatMap(c => {
    const pick = productById(c.pickId);
    return pick ? [{
      creator: c,
      pick
    }] : [];
  });
  if (picks.length === 0 && !hasPress()) return null;
  return <Section tone="sand" labelledBy={picks.length > 0 ? 'collectief' : undefined} label={`Media over ${founder.firstName}`} contained={false} data-id="element-5600" data-mp="1vny93diizfed">
      <div className="mx-auto max-w-content px-6 lg:px-10" data-id="element-5601" data-mp="y3bvxkd8opvn">
        <PressStrip data-id="element-5602" data-mp="1313psjxg2m2h" />

        {picks.length > 0 && <>
        <div className={`flex flex-wrap items-end justify-between gap-6 ${hasPress() ? 'mt-14 lg:mt-16' : ''}`} data-id="element-5603" data-mp="17znfmvf5si04">
          <h2 id="collectief" className={`max-w-2xl ${sectionHeading} text-ink`} data-id="element-5604" data-mp="1c18w0qqwjef2">
            {fill(heading)}
          </h2>
          {body && <p className="max-w-sm text-[17px] leading-relaxed text-muted" data-id="element-5605" data-mp="2je10drem8hbx">{fill(body)}</p>}
        </div>

        <ul className="mt-14 grid gap-8 md:grid-cols-3" data-id="element-5606" data-mp="2nech4t3uxxi4">
          {picks.map(({
            creator: c,
            pick
          }) => {
            const soldOut = isSoldOut(pick);
            const path = `/webshop/${pick.id}`;
            return <li key={c.handle} className="h-full" data-id="element-5607" data-mp="jennjpvpezgd">
                <article className="flex h-full flex-col bg-shell" aria-label={c.name} data-id="element-5608" data-mp="xaspt0gn7jgh">
                  <img src={c.image} alt={c.name} loading="lazy" className="aspect-[4/5] w-full object-cover" data-id="element-5609" data-mp="se29cc0xeluj" />
                  <div className="flex flex-1 flex-col p-6" data-id="element-5610" data-mp="4o7yu142x9dg">
                    <h3 className="type-name text-ink" data-id="element-5611" data-mp="m338wu2m2o6">{c.name}</h3>
                    <p className="mt-0.5 text-[13px] text-muted" data-id="element-5612" data-mp="jqi6at0nc6w">
                      {c.handle} · {c.role}
                    </p>
                    <blockquote className="mt-4 text-[15px] leading-relaxed text-muted" data-id="element-5613" data-mp="9lzpyswwgrgl">“{c.quote}”</blockquote>

                    <div className="mt-auto pt-6" data-id="element-5614" data-mp="3i1jdrcesxho9">
                      <div className="flex items-center gap-3 border-t border-line pt-4" data-id="element-5615" data-mp="3hzyj7mshlyqe">
                        {/* Dezelfde bestemming als de naam ernaast: niet dubbel in de tabvolgorde. */}
                        <Link to={path} className="shrink-0" tabIndex={-1} aria-hidden="true" data-id="element-5616" data-mp="1sj307vvnjr0s">
                          <ProductImage src={pick.image} alt="" className="h-14 w-12 shrink-0 p-1" data-id="element-5617" data-mp="1xe0h1oz6b4o8" />
                        </Link>
                        <div className="min-w-0 flex-1" data-id="element-5618" data-mp="1ojcck11f9r0h">
                          <p className="text-[13px] text-clay" data-id="element-5619" data-mp="1oju4wmkyrl34">Haar keuze</p>
                          <Link to={path} className="block text-[15px] leading-snug text-ink transition-colors duration-150 ease-soft hover:text-clay" data-id="element-5620" data-mp="2cahtkjj3mpcd">
                            {pick.name}
                          </Link>
                          <p className="text-[13px] text-muted" data-id="element-5621" data-mp="22aqu03folk5f">
                            {euro(pick.price)}
                            {soldOut && <span className="text-clay" data-id="element-5622" data-mp="qvtvm2xlxile"> · {shopLabels.soldOutShort}</span>}
                          </p>
                        </div>
                        <AddButton product={pick} data-id="element-5623" data-mp="vvpxnwmquzyb" />
                      </div>
                      <p className="mt-3 text-[13px] text-muted" data-id="element-5624" data-mp="1b4tnayskogll">
                        Code <span className="text-ink" data-id="element-5625" data-mp="16dv33o4gqyd2">{c.code}</span>: reisformaat cadeau bij je bestelling
                      </p>
                    </div>
                  </div>
                </article>
              </li>;
          })}
        </ul>
          </>}
      </div>
    </Section>;
}